import React, { useState, useContext } from 'react';
import { motion } from 'framer-motion';

import WavyLine from '../Components/wavyline';
import SEOHead from '../Components/SEOHead';
import PageBreadcrumbs from '../Components/PageBreadcrumbs';
import EmailStatus from '../Components/EmailStatus';
import { DataContext } from '../Components/DataContext';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaPaperPlane, FaUser, FaEnvelope as FaEmail, FaMobile, FaCheckCircle, FaCheck } from 'react-icons/fa';
import formService from '../services/formService';
import { validateConsultationForm, sanitizeFormData } from '../utils/formValidation';

const ConsultationForm = () => {
  console.log('🏥 CONSULTATION FORM COMPONENT LOADED');
  const data = useContext(DataContext);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    postcode: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Use default values if data is not loaded yet or missing
  const consultationData = data?.ConsultationFormPage || {};
  const { hero, form, contactInfo } = consultationData;
  
  // Default values if data is missing
  const defaultHero = {
    title: 'Book Your Consultation',
    subtitle: 'Professional Podiatry Care in the Comfort of Your Home',
    description: 'Experience expert foot care and treatment tailored to your needs. Our experienced podiatrist provides comprehensive consultations right at your doorstep.'
  };
  
  const defaultForm = {
    title: 'Book Your Consultation',
    subtitle: 'Fill out the form below and we\'ll get back to you as soon as possible',
    fields: {
      name: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      postcode: 'Postcode',
      message: 'Message'
    }
  };
  
  const defaultContactInfo = {
    phone: {
      label: 'Phone',
      value: '07809 267586',
      link: 'tel:07809267586'
    },
    email: {
      label: 'Email',
      value: 'info@droshpod.com',
      link: 'mailto:info@droshpod.com'
    },
    address: {
      label: 'Service Area',
      value: 'Manchester & Surrounding Areas'
    }
  };
  
  const finalHero = hero || defaultHero;
  const finalForm = form || defaultForm;
  const finalContactInfo = contactInfo || defaultContactInfo;

  // SEO structured data for Consultation Form page
  const consultationFormStructuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Book Consultation - Drosh The Pod Manchester",
    "description": "Book your podiatry consultation in Manchester. Contact our experienced podiatrists for professional foot care and treatment.",
    "url": "https://www.droshpod.com/consultationform",
    "mainEntity": {
      "@type": "Organization",
      "name": "Drosh The Pod",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "07809-267586",
        "contactType": "customer service",
        "email": "info@droshpod.com"
      },
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Manchester",
        "addressRegion": "Greater Manchester",
        "addressCountry": "GB"
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Validate form data
    const validationErrors = validateConsultationForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      // Sanitize form data
      const sanitizedData = sanitizeFormData(formData);
      console.log('Original form data:', formData);
      console.log('Sanitized form data:', sanitizedData);
      
      // Submit form using service
      console.log('🏥 CONSULTATION FORM: About to submit with data:', sanitizedData);
      const result = await formService.submitConsultationForm(sanitizedData);
      console.log('🏥 CONSULTATION FORM: Form submitted successfully:', result);
      
      setSubmitStatus('success');
      // Reset form data
      setFormData({
        name: '',
        email: '',
        phone: '',
        postcode: '',
        message: ''
      });
      // Scroll to top of form to show success message (after state update)
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead 
        title="Book Home Consultation | Manchester Podiatry"
        description="Book your home podiatry consultation in Manchester. Our experienced podiatrist provides professional foot care directly to your home. Get in touch today."
        keywords="book home consultation manchester, home podiatry appointment, foot care consultation, manchester podiatrist, book podiatry home visit"
        canonical="https://www.droshpod.com/consultation-form"
        structuredData={consultationFormStructuredData}
      />
      
      <main className="min-h-screen">
        {/* Breadcrumbs */}
        <div className="container mx-auto px-4 pt-4 pb-2">
          <PageBreadcrumbs />
        </div>

        {/* Hero Section */}
        <section className="relative py-12 bg-gradient-to-br from-primary/10 via-white to-secondary/10">
          <div className="absolute inset-0 top-1/2 overflow-hidden -z-10">
            <WavyLine />
          </div>
          
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto"
            >
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">
                {finalHero.title}
              </h1>
              <p className="text-xl md:text-2xl text-gray-700 mb-3 font-medium">
                {finalHero.subtitle}
              </p>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {finalHero.description}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {/* Contact Information */}
              <motion.div
                initial={{ x: -50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="lg:col-span-1"
              >
                <div className="bg-gray-100 text-gray-800 p-6 rounded-2xl shadow-xl border border-gray-200">
                  <h2 className="text-xl font-bold mb-4">
                    {finalContactInfo.title || 'Other Ways to Reach Us'}
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <FaPhone className="text-2xl mr-4 text-gray-600" />
                      <div>
                        <p className="font-semibold text-gray-800">{finalContactInfo.phone.label}</p>
                        <a 
                          href={finalContactInfo.phone.link}
                          className="text-gray-700 hover:text-gray-900 transition-colors"
                        >
                          {finalContactInfo.phone.value}
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <FaEmail className="text-2xl mr-4 text-gray-600" />
                      <div>
                        <p className="font-semibold text-gray-800">{finalContactInfo.email.label}</p>
                        <a 
                          href={finalContactInfo.email.link}
                          className="text-gray-700 hover:text-gray-900 transition-colors"
                        >
                          {finalContactInfo.email.value}
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <FaMapMarkerAlt className="text-2xl mr-4 text-gray-600" />
                      <div>
                        <p className="font-semibold text-gray-800">{finalContactInfo.address.label}</p>
                        <p className="text-gray-700">{finalContactInfo.address.value}</p>
                      </div>
                    </div>
                   </div>

                  <div className="mt-6 pt-4 border-t border-gray-300">
                    <h3 className="text-base font-semibold mb-3 text-gray-800">Why Choose Us?</h3>
                    <ul className="space-y-1.5 text-sm text-gray-700">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-gray-600 rounded-full mr-3"></span>
                        Expert podiatrist with specialised training
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-gray-600 rounded-full mr-3"></span>
                        Convenient home visit service
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-gray-600 rounded-full mr-3"></span>
                        Personalised treatment plans
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-gray-600 rounded-full mr-3"></span>
                        Professional care in your own home
                      </li>
                    </ul>
                  </div>
                </div>
              </motion.div>

              {/* Contact Form */}
              <motion.div
                initial={{ x: 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className="lg:col-span-2"
              >
                <div className="bg-white p-8 rounded-2xl shadow-xl border-2 border-gray-200">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      {finalForm.title}
                    </h2>
                    <p className="text-base text-gray-600">
                      {finalForm.subtitle}
                    </p>
                  </div>

                  {/* Success/Error Messages */}
                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                      className="mb-6 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl shadow-lg"
                    >
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                            <FaCheck className="text-white text-xl" />
                          </div>
                        </div>
                        <div className="flex-1 ml-4">
                          <h3 className="text-xl font-bold text-green-800 mb-2">
                            Consultation Request Submitted Successfully!
                          </h3>
                          <p className="text-green-700 text-base mb-3">
                            Thank you for your consultation request. We've sent you a confirmation email and will get back to you within 24 hours during business days.
                          </p>
                          <div className="bg-white/60 rounded-lg p-3 mt-3">
                            <p className="text-sm text-green-800 font-semibold mb-1">What happens next?</p>
                            <ul className="text-sm text-green-700 space-y-1">
                              <li className="flex items-center">
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></span>
                                Check your email for confirmation
                              </li>
                              <li className="flex items-center">
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></span>
                                We'll review your request and contact you soon
                              </li>
                              <li className="flex items-center">
                                <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></span>
                                For urgent matters, call us at <a href="tel:07809267586" className="font-semibold underline">07809 267586</a>
                              </li>
                            </ul>
                          </div>
                        </div>
                        <button
                          onClick={() => setSubmitStatus(null)}
                          className="flex-shrink-0 ml-4 text-green-600 hover:text-green-800 transition-colors"
                          aria-label="Close message"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {submitStatus === 'error' && (
                    <EmailStatus
                      status={submitStatus}
                      message="Something went wrong. Please try again or contact us directly at 07809 267586."
                      onClose={() => setSubmitStatus(null)}
                    />
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-gray-800 mb-2">
                          {finalForm.fields?.name || 'Full Name'} *
                        </label>
                        <div className="relative">
                          <FaUser className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`w-full pl-12 pr-4 py-3 rounded-lg border-2 transition-all bg-white text-gray-900 ${
                              errors.name 
                                ? 'border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500' 
                                : 'border-gray-300 focus:ring-2 focus:ring-primary focus:border-primary'
                            }`}
                            required
                            placeholder="Enter your full name"
                          />
                        </div>
                        {errors.name && <p className="text-red-500 text-sm mt-1.5">{errors.name}</p>}
                      </div>
                      
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-gray-800 mb-2">
                          {finalForm.fields?.email || 'Email Address'} *
                        </label>
                        <div className="relative">
                          <FaEnvelope className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={`w-full pl-12 pr-4 py-3 rounded-lg border-2 transition-all bg-white text-gray-900 ${
                              errors.email 
                                ? 'border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500' 
                                : 'border-gray-300 focus:ring-2 focus:ring-primary focus:border-primary'
                            }`}
                            required
                            placeholder="your.email@example.com"
                          />
                        </div>
                        {errors.email && <p className="text-red-500 text-sm mt-1.5">{errors.email}</p>}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="phone" className="block text-sm font-semibold text-gray-800 mb-2">
                          {finalForm.fields?.phone || 'Phone Number'}
                        </label>
                        <div className="relative">
                          <FaMobile className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className={`w-full pl-12 pr-4 py-3 rounded-lg border-2 transition-all bg-white text-gray-900 ${
                              errors.phone 
                                ? 'border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500' 
                                : 'border-gray-300 focus:ring-2 focus:ring-primary focus:border-primary'
                            }`}
                            placeholder="07809 267586"
                          />
                        </div>
                        {errors.phone && <p className="text-red-500 text-sm mt-1.5">{errors.phone}</p>}
                      </div>
                      
                      <div>
                        <label htmlFor="postcode" className="block text-sm font-semibold text-gray-800 mb-2">
                          {finalForm.fields?.postcode || 'Postcode'}
                        </label>
                        <div className="relative">
                          <FaMapMarkerAlt className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
                          <input
                            type="text"
                            id="postcode"
                            name="postcode"
                            value={formData.postcode}
                            onChange={handleChange}
                            className={`w-full pl-12 pr-4 py-3 rounded-lg border-2 transition-all bg-white text-gray-900 ${
                              errors.postcode 
                                ? 'border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500' 
                                : 'border-gray-300 focus:ring-2 focus:ring-primary focus:border-primary'
                            }`}
                            placeholder="M1 1AA"
                          />
                        </div>
                        {errors.postcode && <p className="text-red-500 text-sm mt-1.5">{errors.postcode}</p>}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                        {finalForm.fields?.message || 'Message'} *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows="5"
                        value={formData.message}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-lg border transition-colors resize-none bg-white text-gray-900 ${
                          errors.message 
                            ? 'border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500' 
                            : 'border-gray-300 focus:ring-2 focus:ring-primary focus:border-primary'
                        }`}
                        required
                        placeholder="Tell us about your foot health concerns, any symptoms you're experiencing, or what you'd like to discuss during your consultation..."
                      ></textarea>
                      {errors.message && <p className="text-red-500 text-sm mt-1.5">{errors.message}</p>}
                    </div>

                    {/* Submit Button - Prominent and Clear */}
                    <div className="pt-4">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-gradient-to-r from-primary to-secondary text-white py-4 px-8 rounded-lg font-bold text-lg hover:from-secondary hover:to-primary transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98]"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                            <span>Sending Your Request...</span>
                          </>
                        ) : (
                          <>
                            <FaPaperPlane className="mr-3 text-xl" />
                            <span>{finalForm.fields?.submit || 'Submit Consultation Request'}</span>
                          </>
                        )}
                      </button>
                      
                      <p className="mt-4 text-center text-sm text-gray-600">
                        <span className="font-semibold">We typically respond within 24 hours</span> during business days.
                        <br />
                        <span className="text-gray-500">For urgent matters, please call us directly at <a href="tel:07809267586" className="text-primary hover:underline font-semibold">07809 267586</a></span>
                      </p>
                    </div>
                  </form>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Additional Information Section - Badge Cards */}
        <section className="py-16 bg-gradient-to-br from-primary/20 via-secondary/15 to-primary/20 relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-64 h-64 bg-secondary rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-secondary/30 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center max-w-5xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Ready to Start Your Journey to Better Foot Health?
              </h2>
              <p className="text-lg md:text-xl text-gray-700 mb-12 max-w-3xl mx-auto">
                Don't let foot pain hold you back. Book your consultation today and take the first step towards pain-free walking and improved mobility.
              </p>
               
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                  className="text-center p-8 bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border-2 border-primary/20 hover:border-primary/40 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <FaUser className="text-white text-2xl" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Assessment</h3>
                  <p className="text-base text-gray-700 leading-relaxed">Comprehensive evaluation of your foot health and concerns.</p>
                </motion.div>
                 
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="text-center p-8 bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border-2 border-primary/20 hover:border-primary/40 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-secondary to-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <FaPaperPlane className="text-white text-2xl" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Personalised Plan</h3>
                  <p className="text-base text-gray-700 leading-relaxed">Tailored treatment recommendations for your specific needs.</p>
                </motion.div>
                 
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                  className="text-center p-8 bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border-2 border-primary/20 hover:border-primary/40 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <FaPhone className="text-white text-2xl" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Ongoing Support</h3>
                  <p className="text-base text-gray-700 leading-relaxed">Continued care and support throughout your treatment journey.</p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
};

export default ConsultationForm;